import * as d3 from 'd3';
import { useEffect, useRef, useState } from 'react';

import { useIntl } from '../../hooks';
import { getStatusTranslationKey } from '../../services/detection-status.js';
import { DetectionGroup, DetectionStatus, SignalDirection } from '../../types';

interface DetectionsSequencesSunburstProps {
  data: DetectionGroup[];
  width?: number;
  height?: number;
}

interface SequenceNode {
  name: string;
  status?: DetectionStatus;
  children?: SequenceNode[];
  value?: number;
}

type HierarchyNode = d3.HierarchyRectangularNode<SequenceNode>;

const FREQUENCY_RANGES = [
  { min: 0, max: 999_999_999, label: '0-1' },
  { min: 1_000_000_000, max: 1_999_999_999, label: '1-2' },
  { min: 2_000_000_000, max: 2_999_999_999, label: '2-3' },
  { min: 3_000_000_000, max: 3_999_999_999, label: '3-4' },
  { min: 4_000_000_000, max: 4_999_999_999, label: '4-5' },
  { min: 5_000_000_000, max: 5_999_999_999, label: '5-6' },
  { min: 6_000_000_000, max: 6_999_999_999, label: '6-7' },
];

export const STATUS_COLORS: Record<DetectionStatus, string> = {
  [DetectionStatus.DETECTED]: 'oklch(0.6 0.15 250)', // Blue
  [DetectionStatus.SUPPRESSION_SUCCESS]: 'oklch(0.723 0.219 149.579)', // Green
  [DetectionStatus.SUPPRESSION_FAILED]: 'oklch(0.637 0.237 25.331)', // Red
  [DetectionStatus.SUPPRESSION_INITIATED]: 'oklch(0.75 0.15 85)', // Yellow-light
  [DetectionStatus.SUPPRESSION_IN_PROGRESS]: 'oklch(0.7 0.18 70)', // Orange/Warning
  [DetectionStatus.WHITELISTED]: 'oklch(0.7 0.1 265)', // Purple/Outline
  [DetectionStatus.LOST]: 'oklch(0.6 0.05 265)', // Gray
};

function lightenOklchColor(oklchString: string, lightnessIncrease: number): string {
  const match = oklchString.match(/oklch\(([\d.]+)\s+([\d.]+)\s+([\d.]+)\)/);
  if (!match) return oklchString;

  const [, l, c, h] = match;
  const newLightness = Math.min(1, parseFloat(l) + lightnessIncrease);
  const newChroma = Math.max(0, parseFloat(c) * 1.2); // Reduce chroma for lighter appearance

  return `oklch(${newLightness.toFixed(3)} ${newChroma.toFixed(3)} ${h})`;
}

function getNodeColor(d: HierarchyNode): string {
  const ancestors = d.ancestors().reverse();

  if (ancestors.length <= 1) return 'oklch(0.5 0.05 265)';

  const statusNode = ancestors[1];
  const status = statusNode.data.status;
  const baseColor = status ? STATUS_COLORS[status] : 'oklch(0.5 0.05 265)';

  if (d.depth === 1) {
    return baseColor;
  } else if (d.depth === 2) {
    return lightenOklchColor(baseColor, 0.2);
  } else if (d.depth === 3) {
    return lightenOklchColor(baseColor, 0.3);
  } else if (d.depth === 4) {
    return lightenOklchColor(baseColor, 0.4);
  }

  return baseColor;
}

interface StatusData {
  label: string;
  status: DetectionStatus;
  directionMap: Map<string, Map<string, Map<string, number>>>;
}

function transformDataToSequences(
  groups: DetectionGroup[],
  intl: ReturnType<typeof useIntl>,
): SequenceNode {
  const root: SequenceNode = {
    name: '',
    children: [],
  };

  const sequenceMap = new Map<DetectionStatus, StatusData>();

  groups.forEach((group) => {
    const statusLabel = intl.formatMessage({ id: getStatusTranslationKey(group.status) });
    const direction = intl.formatMessage({
      id: `detections.table.directions.${group.direction ?? SignalDirection.UNKNOWN}`,
    });
    const signalType = intl.formatMessage({ id: `signalType.${group.signalType}` }) || 'Unknown';
    const range = FREQUENCY_RANGES.find((r) => group.frequency >= r.min && group.frequency < r.max);
    const frequencyRangeHumanName = intl.formatMessage({
      id: `detectionsSunburst.frequencyRanges.${range?.label}`,
    });

    if (!sequenceMap.has(group.status)) {
      sequenceMap.set(group.status, {
        label: statusLabel,
        status: group.status,
        directionMap: new Map(),
      });
    }
    const statusData = sequenceMap.get(group.status)!;
    const directionMap = statusData.directionMap;

    if (!directionMap.has(direction)) {
      directionMap.set(direction, new Map());
    }
    const signalTypeMap = directionMap.get(direction)!;

    if (!signalTypeMap.has(signalType)) {
      signalTypeMap.set(signalType, new Map());
    }
    const frequencyMap = signalTypeMap.get(signalType)!;

    const currentCount = frequencyMap.get(frequencyRangeHumanName) || 0;
    frequencyMap.set(frequencyRangeHumanName, currentCount + 1);
  });

  const statusNodes: SequenceNode[] = [];

  sequenceMap.forEach((statusData) => {
    const directionNodes: SequenceNode[] = [];

    statusData.directionMap.forEach((signalTypeMap, direction) => {
      const signalTypeNodes: SequenceNode[] = [];

      signalTypeMap.forEach((frequencyMap, signalType) => {
        const frequencyNodes: SequenceNode[] = [];

        frequencyMap.forEach((count, frequencyRange) => {
          frequencyNodes.push({
            name: frequencyRange,
            value: count,
          });
        });

        signalTypeNodes.push({
          name: signalType,
          children: frequencyNodes,
        });
      });

      directionNodes.push({
        name: direction,
        children: signalTypeNodes,
      });
    });

    statusNodes.push({
      name: statusData.label,
      status: statusData.status,
      children: directionNodes,
    });
  });

  root.children = statusNodes;
  return root;
}

export function DetectionsSequencesSunburst({
  data,
  width = 928,
  height = 928,
}: DetectionsSequencesSunburstProps) {
  const intl = useIntl();
  const svgRef = useRef<SVGSVGElement>(null);
  const breadcrumbRef = useRef<HTMLDivElement>(null);
  const [selectedSequence, setSelectedSequence] = useState<string>('');
  const [selectedPercentage, setSelectedPercentage] = useState<string>('');
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    if (!svgRef.current || data.length === 0) {
      if (svgRef.current) {
        d3.select(svgRef.current).selectAll('*').remove();
      }
      return;
    }

    const hierarchyData = transformDataToSequences(data, intl);
    const radius = Math.min(width, height) / 2;
    const hierarchy = d3
      .hierarchy(hierarchyData)
      .sum((d) => d.value || 0)
      .sort((a, b) => (b.value || 0) - (a.value || 0));
    const root = d3.partition<SequenceNode>().size([2 * Math.PI, radius])(hierarchy);

    setTotalCount(root.value ?? totalCount);

    const arc = d3
      .arc<HierarchyNode>()
      .startAngle((d) => d.x0)
      .endAngle((d) => d.x1)
      .padAngle((d) => Math.min((d.x1 - d.x0) / 2, 0.005))
      .padRadius(radius / 2)
      .innerRadius((d) => d.y0)
      .outerRadius((d) => d.y1 - 1);
    const svg = d3
      .select(svgRef.current)
      .attr('viewBox', [-width / 2, -height / 2, width, height])
      .style('font', '12px sans-serif');

    svg.selectAll('*').remove();

    const g = svg.append('g');
    const centerTextGroup = g.append('g').attr('class', 'center-text');
    const centerPercentage = centerTextGroup
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '-0.2em')
      .style('font-size', '32px')
      .style('font-weight', 'bold')
      .style('pointer-events', 'none')
      .style('fill', 'var(--color-foreground)')
      .text('');
    const centerLabel = centerTextGroup
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '1.5em')
      .style('font-size', '14px')
      .style('pointer-events', 'none')
      .style('fill', 'var(--color-foreground)')
      .text('');
    const path = g
      .selectAll('path')
      .data(
        root.descendants().filter((d) => {
          return d.depth > 0;
        }),
      )
      .join('path')
      .attr('fill', (d) => getNodeColor(d))
      .attr('fill-opacity', 0.8)
      .attr('stroke', '#fff')
      .attr('stroke-width', 1)
      .attr('d', arc)
      .style('cursor', 'pointer')
      .on('mouseover', function (_event, d) {
        const sequenceArray = d.ancestors().reverse();
        sequenceArray.shift();

        const sequencePath = sequenceArray.map((node) => node.data.name).join(' → ');
        const percentage = ((100 * d.value!) / root.value!).toFixed(2);

        setSelectedSequence(sequencePath);
        setSelectedPercentage(`${d.value}/${root.value}, ${percentage}%`);

        centerPercentage.text(`${percentage}%`);
        centerLabel.text(
          intl.formatMessage({ id: 'detectionsSunburst.centerText' }, { totalCount }),
        );

        path.style('opacity', 0.3);
        path.filter((node) => sequenceArray.indexOf(node) >= 0).style('opacity', 1);
      })
      .on('mouseout', function () {
        path.style('opacity', 1);
        setSelectedSequence('');
        setSelectedPercentage('100%');

        centerPercentage.text('');
      });

    path.append('title').text(`${selectedSequence} ${selectedPercentage}`);

    g.selectAll('text.arc-label')
      .data(
        root.descendants().filter((d) => {
          return d.depth > 0 && d.y1 - d.y0 > 20 && d.x1 - d.x0 > 0.1;
        }),
      )
      .join('text')
      .attr('class', 'arc-label')
      .attr('transform', (d) => {
        const x = (((d.x0 + d.x1) / 2) * 180) / Math.PI;
        const y = (d.y0 + d.y1) / 2;
        return `rotate(${x - 90}) translate(${y},0) rotate(${x < 180 ? 0 : 180})`;
      })
      .attr('dy', '0.35em')
      .attr('text-anchor', 'middle')
      .attr('pointer-events', 'none')
      .attr('fill', 'black')
      .style('font-size', '12px')
      .text((d) => d.data.name);
  }, [data, width, height, intl, selectedSequence, selectedPercentage, totalCount]);

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-foreground">
        {intl.formatMessage({ id: 'detectionsSunburst.noData' })}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div
        ref={breadcrumbRef}
        data-testid="breadcrumb-container"
        className="min-h-[60px] p-4 bg-gray-light rounded-lg border border-border"
      >
        {selectedSequence ? (
          <div className="space-y-2">
            <div className="text-sm font-medium text-foreground text-center">
              {selectedSequence} ({selectedPercentage})
            </div>
          </div>
        ) : (
          <div className="text-sm text-foreground text-center">
            {intl.formatMessage({ id: 'detectionsSunburst.hoverPrompt' }, { totalCount })}
          </div>
        )}
      </div>

      <div className="flex justify-center">
        <svg ref={svgRef} width="100%" height={height} />
      </div>
    </div>
  );
}
